import {Document, Image, Page, StyleSheet, Text, usePDF, View} from "@react-pdf/renderer";
import logo from '../../assets/Catalight-Care-Services-Color.png';
import {
    AuthClosedReasonMap,
    AuthorizationInfo,
    AuthorizationRecordType, AuthorizationRecordTypeMap,
    AuthorizationTierDetail,
    ClientInfo,
    extractAuthorizationTierDetail
} from "../domain";
import {formatDateStr} from "common/util/date";
import { useGlobalSetting } from "security";
import { AUTH_PDF_FOOTER_SETTING_KEY, extractGlobalSetting } from "security";

type DownloadAuthorizationPDFButtonProps = {
    authorization: AuthorizationInfo;
    clientInfo: ClientInfo;
}
export const DownloadAuthorizationPDFButton = ({authorization, clientInfo}: DownloadAuthorizationPDFButtonProps) => {
    return (<DownloadPDFLink authorization={authorization} clientInfo={clientInfo}/>);
}

type DownloadPDFLinkProps = {
    authorization: AuthorizationInfo;
    clientInfo: ClientInfo;
}
const DownloadPDFLink = ({authorization, clientInfo}: DownloadPDFLinkProps) => {
    const globalSettings = useGlobalSetting();
    const footerContent = extractGlobalSetting(AUTH_PDF_FOOTER_SETTING_KEY, globalSettings ?? []);
    const [instance] = usePDF({
        document: <AuthorizationPdfDocument authorization={authorization} clientInfo={clientInfo} footerContent={footerContent}/>
    });

    if (instance.error) return <div>Something went wrong: {instance.error}</div>

    let instanceUrl = "# "
    if (!instance.loading) {
        instanceUrl = instance.url!
    }
    return (
        <a href={instanceUrl}
           download={`${authorization.bhpnAuthNumber}.pdf`}
           className={
               ` portal-provider-fat-atlantis-200-btn
                                    focus:outline-none
                                    rounded-full
                                    whitespace-no-wrap
                                    border-2
                                    w-full
                                    font-semibold
                                    ${instance.loading ? "cursor-not-allowed pointer-events-none opacity-50" : ""}
                                    p-2 text-sm `}>
            Download Auth
        </a>
    )

}


type AuthorizationPdfDocumentProps = {
    authorization: AuthorizationInfo;
    clientInfo: ClientInfo;
    footerContent: string;
}
const AuthorizationPdfDocument = ({
                                      authorization,
                                      clientInfo,
                                      footerContent
                                  }: AuthorizationPdfDocumentProps) => {
    return (<Document title={"Authorization"}>
        <Pages authorization={authorization} clientInfo={clientInfo} footerContent={footerContent}/>
    </Document>)
}

const styles = StyleSheet.create({
    page: {
        flexDirection: 'row',
        fontSize: 12,
        backgroundColor: '#FFFFFF'
    },
    subPage: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignContent: "center"
    },
    section: {
        margin: 10,
        padding: 10,
        flexGrow: 1
    },
    overview: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignContent: "center",
        marginBottom: 20
    },
    textFieldTitle: {
        fontFamily: "Helvetica-Bold",
    },
    textFieldContent: {
        fontFamily: "Helvetica",
    },
    rowContainer: {
        flexDirection: "row",
        flexWrap: "wrap",
    },
    textFieldContainer: {
        padding: 4,
    },
    tierSection: {
        display: "flex",
        marginBottom: 20,
    },
    logo: {
        width: 360,
        height: 60,
        marginBottom: 30
    },
    assessmentTitle: {
        fontSize: 18,
        fontFamily: "Helvetica-Bold",
        marginBottom: 10,
        marginTop: 10
    },
    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#000",
        borderLeftWidth: 1,
        borderLeftColor: "#000",
        borderRightWidth: 1,
        borderRightColor: "#000"
    },
    tableRowGray: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#000",
        borderLeftWidth: 1,
        borderLeftColor: "#000",
        borderRightWidth: 1,
        borderRightColor: "#000",
        backgroundColor: "#E8E8E8"
    },
    tableRowFirst: {
        flexDirection: "row",
        borderWidth: 1,
        borderColor: "#000"
    },
    tableRowFirstGray: {
        flexDirection: "row",
        borderWidth: 1,
        borderColor: "#000",
        backgroundColor: "#E8E8E8"
    },
    tableCell: {
        width: "35%",
        padding: 6,
        borderRightWidth: 1,
        borderRightColor: "#000",
        fontFamily: "Helvetica-Bold",
        fontSize: 11
    },
    tableCellValue: {
        width: "65%",
        padding: 6,
        fontFamily: "Helvetica",
        fontSize: 11
    },
    footer: {
        position: "absolute",
        bottom: 20,
        left: 0,
        right: 0,
        textAlign: "center",
        fontSize: 9,
        color: "#666",
        fontFamily: "Helvetica"
    }
});

type PagesProps = {
    authorization: AuthorizationInfo;
    clientInfo: ClientInfo;
    footerContent: string;
}

const Pages = ({authorization, clientInfo, footerContent}: PagesProps) => {
    const details = extractTierDetailsFromAuthorization(authorization)
    const firstPage = details.slice(0, 3);
    const secondPage = details.slice(3);
    return <>
        <Page size="A4" style={styles.page}>
            <View style={styles.section}>
                <OverviewSection authorization={authorization} clientInfo={clientInfo}/>
                <View style={styles.subPage}>
                    {
                        firstPage.map((detail) =>
                            <TierDetailItem key={detail.tier} detail={detail}/>
                        )
                    }
                </View>
            </View>
            <Footer footerContent={footerContent}/>
        </Page>
        {secondPage.length > 0 &&
            <Page size="A4" style={styles.page}>
                <View style={styles.section}>
                    <View style={styles.subPage}>
                        {
                            secondPage.map((detail) =>
                                <TierDetailItem key={detail.tier} detail={detail}/>
                            )
                        }
                    </View>
                </View>
                <Footer footerContent={footerContent}/>
            </Page>
        }
    </>
}

type OverviewSectionProps = {
    authorization: AuthorizationInfo;
    clientInfo: ClientInfo;
}
const OverviewSection = ({authorization, clientInfo}: OverviewSectionProps) => {
    const isDEAuth = [AuthorizationRecordType.Diagnostic_Evaluation_Authorization].includes(AuthorizationRecordTypeMap.get(authorization?.recordType ?? "") as AuthorizationRecordType);
    const isTherapyTreatment = [AuthorizationRecordType.Therapy_Treatment_Authorization].includes(AuthorizationRecordTypeMap.get(authorization?.recordType ?? "") as AuthorizationRecordType);
    const isAssessmentAuth = [
        AuthorizationRecordType.ABA_Assessment_Authorization,
        AuthorizationRecordType.Psychotherapy_Assessment,
        AuthorizationRecordType.Therapy_Assessment_Authorization
    ].includes(AuthorizationRecordTypeMap.get(authorization?.recordType ?? "") as AuthorizationRecordType);
    
    if (isAssessmentAuth) {
        return <AssessmentSection authorization={authorization} clientInfo={clientInfo}/>
    }
    
    return (
        <View
            style={styles.overview}>
            <Image style={styles.logo} src={logo}/>
            <View style={styles.rowContainer}>
                <TextField title={"Client Name"} content={clientInfo.fullName}/>
                <TextField isDate title={"Date of Birth"} content={clientInfo.birthDate}/>
            </View>
            <View style={styles.rowContainer}>
                <TextField title={"Medical Record Number"} content={clientInfo.medicalRecordNumber}/>
                <TextField title={"Diagnosis"} content={authorization.diagnosis}/>
            </View>
            <View style={styles.rowContainer}>
                <TextField title={"Authorization Number"} content={authorization.bhpnAuthNumber}/>
                {
                    !isTherapyTreatment && <TextField title={"Report Due Date"} isDate content={authorization.reportDueDate}/>
                }
            </View>
            <View style={styles.rowContainer} />
            {
                isTherapyTreatment &&
                <View style={styles.rowContainer}>
                    <TextField title={"Speciality"} content={authorization?.funderAuthSpeciality}/>
                    <TextField title={"Report Due Date"} isDate content={authorization.reportDueDate}/>
                </View>
            }
            <View style={styles.rowContainer}>
                <TextField title={"Start Date"} isDate content={authorization.startDate}/>
                <TextField title={"End Date"} isDate content={authorization.endDate}/>
            </View>
            {
                !isDEAuth && !isTherapyTreatment &&
                <View style={styles.rowContainer}>
                    <TextField title={"Service Line"} content={authorization.serviceLine}/>
                    <TextField title={"Number of Tiers"} content={authorization.numOfTier?.toString()}/>
                </View>
            }
            {
                isTherapyTreatment &&
                <View style={styles.rowContainer}>
                    <TextField title={"Authorized For"} content={`${authorization?.therapyTreatmentUnit?.toString()} ${authorization?.therapyTreatmentUnitType} per ${authorization?.therapyTreatmentUnitsPer}`}/>
                    <TextField title={"Procedure Code"} content={authorization?.therapyTreatmentProcedureCode ?? ""}/>
                </View>
            }
            {
                isDEAuth &&
                <View style={styles.rowContainer}>
                    <TextField title={"Procedure Code"} content={authorization.diagnosticEvaluationProcedureCode}/>
                </View>
            }
            <View style={styles.rowContainer}>
                <TextField title={"Status"} content={authorization?.status}/>
                <TextField title={"Closed Reason"}
                           content={AuthClosedReasonMap.get(authorization?.closedReason) ?? ""}/>
            </View>
        </View>
    );
}

const AssessmentSection = ({authorization, clientInfo}: {authorization: AuthorizationInfo, clientInfo: ClientInfo}) => {
    return (
        <View style={styles.overview}>
            <Image style={styles.logo} src={logo}/>
            <Text style={styles.assessmentTitle}>Assessment Authorization:</Text>
            <View style={styles.tableRowFirst}>
                <Text style={styles.tableCell}>Client Name:</Text>
                <Text style={styles.tableCellValue}>{clientInfo.fullName}</Text>
                </View>
                <View style={styles.tableRowGray}>
                    <Text style={styles.tableCell}>Date of Birth:</Text>
                    <Text style={styles.tableCellValue}>{formatDateStr(clientInfo.birthDate)}</Text>
                </View>
                <View style={styles.tableRow}>
                    <Text style={styles.tableCell}>Medical Record Number:</Text>
                    <Text style={styles.tableCellValue}>{clientInfo.medicalRecordNumber}</Text>
                </View>
                <View style={styles.tableRowGray}>
                    <Text style={styles.tableCell}>Diagnosis:</Text>
                    <Text style={styles.tableCellValue}>{authorization.diagnosis}</Text>
                </View>
                <View style={styles.tableRow}>
                    <Text style={styles.tableCell}>Authorization Number:</Text>
                    <Text style={styles.tableCellValue}>{authorization.bhpnAuthNumber}</Text>
                </View>
                <View style={styles.tableRowGray}>
                    <Text style={styles.tableCell}>Assessment Type:</Text>
                    <Text style={styles.tableCellValue}>{authorization.allServiceType.join(", ")}</Text>
                </View>
                <View style={styles.tableRow}>
                    <Text style={styles.tableCell}>Assessment Procedure Code:</Text>
                    <Text style={styles.tableCellValue}>{authorization.assessmentCode}</Text>
                </View>
                <View style={styles.tableRowGray}>
                    <Text style={styles.tableCell}>Start Date:</Text>
                    <Text style={styles.tableCellValue}>{formatDateStr(authorization.startDate)}</Text>
                </View>
                <View style={styles.tableRow}>
                    <Text style={styles.tableCell}>End Date:</Text>
                    <Text style={styles.tableCellValue}>{formatDateStr(authorization.endDate)}</Text>
                </View>
                <View style={styles.tableRowGray}>
                    <Text style={styles.tableCell}>Report Due Date:</Text>
                    <Text style={styles.tableCellValue}>{formatDateStr(authorization.reportDueDate)}</Text>
                </View>
                <View style={styles.tableRow}>
                    <Text style={styles.tableCell}>Status:</Text>
                    <Text style={styles.tableCellValue}>{authorization?.status}</Text>
                </View>
                <View style={styles.tableRowGray}>
                    <Text style={styles.tableCell}>Closed Reason:</Text>
                    <Text style={styles.tableCellValue}>{AuthClosedReasonMap.get(authorization?.closedReason) ?? ""}</Text>
                </View>
            </View>
    );
}


type TierDetailItemProps = {
    detail: AuthorizationTierDetail;
}
const TierDetailItem = ({detail}: TierDetailItemProps) =>
    <View style={styles.tierSection}>
        <View style={styles.rowContainer}>
            <TextField hasBorder isTop title={"Tier"} content={detail.tier.toString()}/>
            <TextField hasBorder isRight isTop title={"Service Type"} content={detail.serviceType}/>
        </View>
        <View style={styles.rowContainer}>
            <TextField hasBorder isDate title={"Start Date"} content={detail.startDate}/>
            <TextField hasBorder isDate isRight title={"End Date"} content={detail.endDate}/>
        </View>

        {detail.directCareCode !== "None" &&
            <>
                <View style={styles.rowContainer}>
                    <TextField hasBorder isFullWidth title={"Paraprofessional Procedure Code"}
                               content={detail.directCareCode}/>
                </View>
                <View style={styles.rowContainer}>
                    <TextField hasBorder title={"Paraprofessional Minimum Hours Per Month"}
                               content={detail.directCareBeneficialHour}/>
                    <TextField hasBorder isRight title={"Paraprofessional Maximum Hours Per Month"}
                               content={detail.directCareHour}/>
                </View>
            </>
        }
        {detail.midLevelCode !== "None" &&
            <>
                <View style={styles.rowContainer}>
                    <TextField hasBorder isFullWidth title={"Mid Level Procedure Code"} content={detail.midLevelCode}/>
                </View>
                <View style={styles.rowContainer}>
                    <TextField hasBorder title={"Mid-Level Minimum Hours Per Month"}
                               content={detail.midLevelBeneficialHour}/>
                    <TextField hasBorder isRight title={"Mid-Level Maximum Hours Per Month"}
                               content={detail.midLevelHour}/>
                </View>
            </>
        }
        {detail.highLevelCode !== "None" &&
            <>
                <View style={styles.rowContainer}>
                    <TextField hasBorder isFullWidth title={"High-Level Procedure Code"}
                               content={detail.highLevelCode}/>
                </View>
                <View style={styles.rowContainer}>
                    <TextField hasBorder title={"High-Level Minimum Hours Per Month"}
                               content={detail.highLevelBeneficialHour}/>
                    <TextField hasBorder isRight title={"High-Level Maximum Hours Per Month"}
                               content={detail.highLevelHour}/>
                </View>
            </>
        }
        {detail.groupCareCode !== "None" &&
            <>
                <View style={styles.rowContainer}>
                    <TextField hasBorder isFullWidth title={"Group Care Procedure Code"}
                               content={detail.groupCareCode}/>
                </View>
                <View style={styles.rowContainer}>
                    <TextField hasBorder title={"Group Care Minimum Hours Per Month"}
                               content={detail.groupCareBeneficialHour}/>
                    <TextField hasBorder isRight title={"Group Care Maximum Hours Per Month"}
                               content={detail.groupCareHour}/>
                </View>
            </>
        }
    </View>

type TextFieldProps = {
    isTop?: boolean;
    hasBorder?: boolean;
    isRight?: boolean;
    isDate?: boolean;
    title: string;
    isFullWidth?: boolean;
    content: string;
}
const TextField = ({
                       title,
                       content,
                       hasBorder = false,
                       isTop = false,
                       isRight = false,
                       isFullWidth = false,
                       isDate = false
                   }: TextFieldProps) => {
    let style = styles.textFieldContainer;
    if (hasBorder) {
        style = {...style, ...{borderBottom: 1, borderRight: 1}}
        style = isTop ? {...style, ...{borderTop: 1}} : style
        style = isRight ? style : {...style, ...{borderLeft: 1}}
    }
    style = isFullWidth ? {...style, ...{width: "100%", backgroundColor: "#d9d9d9"}} : {...style, ...{width: "50%"}}

    return (
        <View style={style}>
            <Text>
                <Text style={styles.textFieldTitle}>{title}: </Text>
                <Text style={styles.textFieldContent}>{isDate ? formatDateStr(content) : content}</Text>
            </Text>
        </View>
    )
}

const extractTierDetailsFromAuthorization = (authorization: AuthorizationInfo): AuthorizationTierDetail[] => {
    const tiers = [...Array(authorization.numOfTier + 1).keys()].slice(1);
    return tiers
        .map((tier) => extractAuthorizationTierDetail(authorization, tier))
        .filter((detail) => !detail.isDraft);
}

type FooterProps = {
    footerContent: string;
}
const Footer = ({footerContent}: FooterProps) => {
    return (
        <Text style={styles.footer}>{footerContent}</Text>
    );
}
